import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { taskAssignsApi } from '../axios';
import { CurrentUser } from '../axios/currentUser';
import { groupsApi } from '../axios/groups';
import { findingsApi } from '../axios/findings';
import { useUserDataStore } from '../newStore/userDataStore';
import { DeactivatedMobileUserMinimumData } from '../types';
import { UserGroup } from '../types/group';
import type { FilterMetadataMobileUser } from '../types/mobileUser';

export interface FindingsFilterUser {
  id: number;
  name: string;
  inactive: boolean;
}

/** The fields buildFindingsFilterUsers reads from an active mobile user. */
interface ActiveFilterUserSource {
  id: number;
  name?: string | null;
  email?: string | null;
}

/** The fields buildFindingsFilterUsers reads from a deactivated mobile user. */
interface DeactivatedFilterUserSource extends ActiveFilterUserSource {
  last_name?: string | null;
}

interface UseUsersQueryOptions {
  includeDeactivatedUsers?: boolean;
}

export function useUsersQuery(options?: UseUsersQueryOptions) {
  const includeDeactivatedUsers = options?.includeDeactivatedUsers ?? false;

  return useQuery<FilterMetadataMobileUser[]>({
    queryKey: ['allUsers', { includeDeactivatedUsers }],
    queryFn: async (): Promise<FilterMetadataMobileUser[]> => {
      const { mobile_users } = await taskAssignsApi.getLightFilterMetadata(
        includeDeactivatedUsers ? { includeDeactivatedUsers: true } : undefined,
      );
      return mobile_users?.map((user) => ({
        id: user[1],
        name: user[0],
        ...(includeDeactivatedUsers && user.length > 2
          ? { isDeactivated: user[2] === true }
          : {}),
      }));
    },
  });
}

function formatMobileUserDisplayName(user: DeactivatedFilterUserSource) {
  const parts = [user.name, user.last_name].filter(
    (part): part is string => typeof part === 'string' && part.trim().length > 0,
  );
  // Mirror the active-user fallback chain so the display name is never empty/null:
  // composed name -> email -> id. A null name here previously crashed the sort (DS-3267).
  return parts.join(' ').trim() || user.email?.trim() || String(user.id);
}

async function fetchAllDeactivatedMobileUsersForFilter(): Promise<
  DeactivatedMobileUserMinimumData[]
> {
  const allUsers: DeactivatedMobileUserMinimumData[] = [];
  let page = 1;
  let totalPages = 1;

  do {
    const pageData = await findingsApi.getDeactivatedMobileUsersForFindings({ page });
    allUsers.push(...pageData.users);
    totalPages = pageData.total_pages;
    page += 1;
  } while (page <= totalPages && totalPages > 0);

  return allUsers;
}

function groupListContainsCurrentUser(group: UserGroup, currentUser: CurrentUser): boolean {
  return (
    group.users?.some(
      (userInGroup) =>
        userInGroup.mobile_user_id === currentUser.id || userInGroup.email === currentUser.email,
    ) ?? false
  );
}

/** Mobile user IDs that belong to any of the groups (union). */
function unionMobileUserIdsFromGroups(groups: UserGroup[]): Set<number> {
  const ids = new Set<number>();
  groups.forEach((group) => {
    group.users?.forEach((userInGroup) => {
      ids.add(userInGroup.mobile_user_id);
    });
  });
  return ids;
}

/**
 * Merge active + deactivated mobile users into the Findings assignee/creator filter list.
 * Active users win over deactivated on id collision; active users sort before inactive,
 * then alphabetically by display name. Exported for unit testing.
 */
export function buildFindingsFilterUsers(
  activatedUsers: ActiveFilterUserSource[],
  deactivatedUsers: DeactivatedFilterUserSource[],
): FindingsFilterUser[] {
  const activeById = new Map<number, FindingsFilterUser>();
  activatedUsers.forEach((user) => {
    activeById.set(user.id, {
      id: user.id,
      name: user.name?.trim() || user.email?.trim() || String(user.id),
      inactive: false,
    });
  });

  deactivatedUsers.forEach((user) => {
    if (activeById.has(user.id)) {
      return;
    }
    activeById.set(user.id, {
      id: user.id,
      name: formatMobileUserDisplayName(user),
      inactive: true,
    });
  });

  return Array.from(activeById.values()).sort((a, b) => {
    if (a.inactive !== b.inactive) {
      return a.inactive ? 1 : -1;
    }
    return a.name.localeCompare(b.name);
  });
}

/**
 * Active + deactivated mobile users for Findings assignee/creator filters (with inactive flag).
 * Uses the findings-scoped endpoints, which don't require the :users permission — the regular
 * /mobile_users_react endpoints redirect with "No permissions" and leave the filters empty (DS-3588).
 */
export function useFindingsFilterUsersQuery({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['findingsFilterUsers', 'activeAndInactive'],
    queryFn: async (): Promise<FindingsFilterUser[]> => {
      const [activatedUsers, deactivatedUsers] = await Promise.all([
        findingsApi.getAllMobileUsersForFindings(),
        fetchAllDeactivatedMobileUsersForFilter(),
      ]);

      return buildFindingsFilterUsers(activatedUsers, deactivatedUsers);
    },
    enabled,
  });
}

/**
 * All groups in the account. NOTE: `GET /groups` has no permission gate of its
 * own (GroupsController#index only authenticates and scopes by account) and the
 * response includes each group's members with their emails, so any caller that
 * is not itself permission-gated must pass `enabled: false` rather than filter
 * the result after the fact.
 */
export function useUserGroupsQuery({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['userGroups'],
    queryFn: async () => {
      const { groups } = await groupsApi.getGroups();
      return groups;
    },
    enabled,
  });
}

/**
 * Narrows a user list to the current user's groups: when they belong to at least
 * one group, only users in those groups (union across groups) are returned; when
 * they belong to none, the list is unscoped.
 *
 * Findings-only. The AskDataScope "@" panel deliberately does NOT use this: see
 * {@link useMentionActiveUsersQuery} for why group narrowing is wrong there.
 *
 * Note the shape of the rule: belonging to no group WIDENS the result to
 * everything, so this narrows precisely the users who are best connected. That is
 * long-standing Findings behavior and is left as-is, but it is the reason this
 * must not be borrowed by a caller that expects "scoped" to mean "less".
 */
export function scopeUsersToOwnGroups({
  users,
  groups,
  currentUser,
}: {
  users: FindingsFilterUser[];
  groups: UserGroup[];
  currentUser: CurrentUser | null;
}): FindingsFilterUser[] {
  if (!currentUser) {
    return users;
  }

  const groupsWithCurrentUser = groups.filter((group) =>
    groupListContainsCurrentUser(group, currentUser),
  );
  if (groupsWithCurrentUser.length === 0) {
    return users;
  }

  const allowedIds = unionMobileUserIdsFromGroups(groupsWithCurrentUser);
  return users.filter((user) => allowedIds.has(user.id));
}

/**
 * Same as {@link useFindingsFilterUsersQuery}, but scoped to the current user's
 * groups.
 */
export function useFindingsFilterUsersScopedQuery({ enabled = true }: { enabled?: boolean } = {}) {
  const { data: allUsers, isLoading: usersLoading } = useFindingsFilterUsersQuery({ enabled });
  const { data: groupsData, isLoading: groupsLoading } = useUserGroupsQuery({ enabled });
  const currentUser = useUserDataStore((state) => state.currentUser);

  const scopedUsers = useMemo(
    () =>
      scopeUsersToOwnGroups({
        users: allUsers ?? [],
        groups: groupsData ?? [],
        currentUser,
      }),
    [allUsers, groupsData, currentUser],
  );

  return {
    data: scopedUsers,
    isLoading: usersLoading || groupsLoading,
  };
}

/**
 * Active mobile users for the AskDataScope "@" panel.
 *
 * NOT narrowed by group membership, on purpose. The category is already gated to
 * users the backend treats as admins (`User#has_admin_access?`), and for those
 * `Chatbot::ReferenceResolver#scope_for('users')` returns every MobileUser in the
 * account with no group filter at all. Narrowing here made the panel disagree with
 * the backend that validates the send, and it did so asymmetrically: an admin who
 * happened to belong to a group saw only that group, while an admin in no group
 * saw everyone, because {@link scopeUsersToOwnGroups} widens on empty membership.
 * A Super Admin in a one-member group therefore saw a single user while a less
 * privileged admin saw all of them. Whatever restricts this category has to be the
 * `canSeeUsers` gate in useMentionSearch, which mirrors the backend, not a second
 * rule the backend never applies.
 *
 * Shares the `['findingsFilterUsers','active']` cache entry with nothing else, but
 * deliberately does NOT reuse {@link useFindingsFilterUsersQuery}: that one also
 * walks every page of deactivated mobile users sequentially, and this panel
 * discards inactive users anyway, so it was paying for a multi-page fetch on a
 * keystroke path. Names are derived with the same helper, so rows read the same as
 * in the Findings filters.
 */
export function useMentionActiveUsersQuery({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['findingsFilterUsers', 'activeOnly'],
    queryFn: async (): Promise<FindingsFilterUser[]> =>
      buildFindingsFilterUsers(await findingsApi.getAllMobileUsersForFindings(), []),
    enabled,
  });
}

